<template>
  <div class="exercises-list open-exercises">
    <div class="exercises-list__wrapper">
      <div class="exercises-list__header">
        <div class="exercises-list__header-title">Lesson exercises:</div>
      </div>
      <div
        v-for="(exercise, index) in exercises"
        :key="`${exercise.id}${index}`"
        :class="[
          'exercises-list__row',
          !hasAccess(exercise) && 'exercises-list__row--disabled',
        ]"
        @click="openExercise(exercise)"
      >
        <GreenTickIcon
          class="exercises-list__row__tick-icon"
          :class="{
            'exercises-list__row__tick-icon--gray': !isCompleted(
              exercise.user_status
            ),
          }"
        />

        <div class="exercises-list__row__name">
          {{ index + 1 }}. {{ exercise.name }}
        </div>
        <div class="exercises-list__row-status__wrapper">
          <PlayIcon
            v-tooltip="{
              content: exercise.user_status,
              placement: 'right-center',
            }"
            class="exercises-list__row-status"
          />
        </div>
      </div>
    </div>
    <div class="exercises-list__modal-wrapper">
      <ExerciseModal
        v-if="isModalShown"
        :exercise="currentExercise"
        :exercisesList="exercises"
        :context="parentType"
        @close-modal="closeModal"
        @previous-exercise="previousExercise()"
        @next-exercise="nextExercise()"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isFreeContent } from '@/helpers'
import PlayIcon from '@/assets/icons/course/exercise/exercise-play-icon.svg'
import GreenTickIcon from '@/assets/icons/course/green_tick.svg'
import ExerciseModal from '@/components/modals/exercise-modal/ExerciseModal.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'ExercisesList',
  components: {
    PlayIcon: { ...PlayIcon, name: 'PlayIcon' },
    GreenTickIcon: { ...GreenTickIcon, name: 'GreenTickIcon' },
    ExerciseModal,
  },
  props: {
    exercises: {
      type: Array,
      required: true,
    },
    isUser: {
      type: Boolean,
      required: true,
    },
    parentType: {
      type: String,
      required: true,
    },
    parentId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isModalShown: false,
      isLoading: true,
    }
  },
  computed: {
    ...mapState('exercises', ['currentExercise']),
  },
  mounted() {
    this.$scrollTo('.open-exercises', 1000, {
      container: 'body',
      offset: -220,
    })
  },
  methods: {
    ...mapActions('exercises', [
      'setCurrentExercise',
      'clearExerciseStore',
      'setExerciseStatus',
      'runExerciseAttempt',
    ]),
    ...mapActions('learningPaths', ['getCurrentLearningPath']),
    ...mapActions('courses', ['getCourseById']),
    ...mapActions('playlists', ['getPlaylistById']),
    async openExercise(exercise) {
      this.setCurrentExercise(exercise)
      await this.setExerciseStatus({
        exercise,
        status: CONSTANTS.CONTENT_USER_STATUS.STARTED,
      })
      this.isModalShown = true
    },
    twoDigitsIndex(index) {
      return index < 10 ? `0${index}` : index
    },
    isCompleted(status) {
      return [
        CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        CONSTANTS.CONTENT_USER_STATUS.STARTED,
      ].includes(status)
    },
    closeModal() {
      if (this.parentId) this.updateParent()
      this.isModalShown = false
      this.clearExerciseStore()
    },
    updateParent() {
      const { LEARNING_PATH, COURSE, PLAYLIST } = CONSTANTS.CONTENT_TYPES
      switch (this.parentType) {
        case LEARNING_PATH:
          this.getCurrentLearningPath(this.parentId)
          break
        case COURSE:
          this.getCourseById(this.parentId)
          break
        case PLAYLIST:
          this.getPlaylistById({
            id: this.parentId,
            refresh: true,
          })
          break
        default:
          break
      }
    },
    previousExercise() {
      const index = this.exercises.indexOf(this.currentExercise)
      if (index > 0) {
        this.openExercise(this.exercises[index - 1])
      }
    },
    nextExercise() {
      const index = this.exercises.indexOf(this.currentExercise)
      if (index < this.exercises.length - 1) {
        this.openExercise(this.exercises[index + 1])
      }
    },
    hasAccess(content) {
      if (this.isUser) return true
      return isFreeContent(content)
    },
  },
}
</script>

<style lang="scss" scoped>
.exercises-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
  color: var(--white);
  font-size: 16px;
  font-family: var(--font-medium);
  &__header {
    display: flex;
    align-items: center;
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 10px;
    width: 100%;
  }
  &__row {
    $row: &;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    border-radius: 5px;
    transition: all 0.5s ease;
    display: flex;
    cursor: pointer;
    padding: 16px 20px;
    width: 100%;
    margin-bottom: 5px;
    align-items: center;
    &-status {
      &__wrapper {
        margin-left: auto;
        padding: 8px;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    &:hover {
      background: linear-gradient(
        112.2deg,
        rgba(187, 188, 199, 0.4) -46.39%,
        rgba(187, 188, 199, 0) 112.38%
      );
      box-shadow: 0px 0px 1px rgba(255, 255, 255, 0.5);
      #{$row}-status__wrapper {
        background: var(--white);
        box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
        border-radius: 5px;
        path {
          stroke: var(--charcoal);
        }
      }

      #{$row}__tick-icon--gray {
        path {
          stroke: #aaaaaa;
        }
      }
    }
    &--disabled {
      pointer-events: none;
      opacity: 0.4;
    }
    &__name {
      margin: 0px 20px;
      font-size: 14px;
    }
    &__tick-icon--gray {
      path {
        stroke: var(--emperor-gray);
      }
    }
  }
}
</style>
